import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

/**
 * The suite's shared sign-in screen: `<suite-gate>` (gate.js, served by the hub
 * next to chrome.js) is the one card every application mounts. This page only
 * wires the element's submits into Sak's own auth state — the card's look,
 * wording and OAuth buttons are edited centrally in gate.js.
 */

/** The suite gate element (defined by gate.js) — `setAuthError` is its own method. */
interface SuiteGateElement extends HTMLElement {
  app: string;
  appName: string;
  setAuthError: (message: string) => void;
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const gate = document.createElement('suite-gate') as SuiteGateElement;
    gate.app = 'sak';
    gate.appName = 'Sak';
    host.appendChild(gate);

    const onCredentials = (e: Event) => {
      const detail = (
        e as CustomEvent<{
          mode: 'login' | 'register';
          email: string;
          password: string;
          name: string | null;
        }>
      ).detail;
      e.preventDefault();
      const attempt =
        detail.mode === 'login'
          ? login(detail.email, detail.password)
          : register(detail.name ?? '', detail.email, detail.password);
      attempt
        .then(() => navigate('/dashboard'))
        .catch((err: unknown) =>
          gate.setAuthError(err instanceof Error ? err.message : 'An error occurred. Please try again.'),
        );
    };
    window.addEventListener('suite-gate:credentials', onCredentials);
    return () => {
      window.removeEventListener('suite-gate:credentials', onCredentials);
      gate.remove();
    };
  }, [login, register, navigate]);

  return <div ref={hostRef} />;
}
